import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldX, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { AccessRequestSection } from '@/components/shared/AccessRequestSection';

interface AccessDeniedDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AccessDeniedDialog({ open, onOpenChange }: AccessDeniedDialogProps) {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { user, profile } = useAuth();

  const handleClose = () => {
    // Always redirect to a safe page (dashboard) instead of staying on the
    // forbidden route — dismissing the dialog must NOT leave the user free
    // to interact with a module they no longer have access to.
    if (onOpenChange) {
      onOpenChange(false);
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto text-center scrollbar-thin" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="items-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mb-2">
            <ShieldX className="h-7 w-7 text-destructive" />
          </div>
          <DialogTitle className="text-xl">Accès refusé</DialogTitle>
          <DialogDescription className="text-sm mt-1">
            Vous n'avez pas les permissions nécessaires pour accéder à cette section.
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs text-muted-foreground mt-1">
          Si vous pensez qu'il s'agit d'une erreur, veuillez contacter un administrateur
          de votre workspace pour obtenir les accès appropriés.
        </p>

        <div className="flex flex-col gap-2 mt-4">
          <Button onClick={handleClose} className="w-full">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
        </div>

        <AccessRequestSection
          orgId={currentOrganization?.id}
          orgName={currentOrganization?.name}
          requesterEmail={user?.email}
          requesterName={profile?.full_name}
        />
      </DialogContent>
    </Dialog>
  );
}
